import { InputBaseComponentProps, SliderProps } from '@mui/material';
import groupBy from 'lodash/groupBy';
import { TFunction } from 'react-i18next';
import { TENANT_NAME } from 'src/config';
import { DataApiInputParams } from 'src/types/api';
import { IAiModel, StringKeys } from 'src/types/base';
import { ChatData } from 'src/types/chat';
import {
  CHAT_CLASS,
  CHAT_SOURCE_TYPES,
  LOOKUP,
  RESOURCE_TYPE
} from 'src/types/enum';
import { ILoggedInUser } from 'src/types/user';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import {
  CanvasMsgObj,
  ChatFile,
  ChatHistoryItem,
  ChatResourcesParams,
  IResponseFeedback,
  RegenerateMsgObj
} from './types';
import { sanitizedFiles } from 'src/utils/utils';
import {
  AssistantContextType,
  AssistantType
} from 'src/redux/slices/assistant';
import {
  CHAT_TOOL_EXECUTED,
  CHAT_QUERY_SUBMITTED,
  TOOL_WEB_SEARCH,
  TOOL_PRO_SEARCH,
  TOOL_IMAGE_GENERATION,
  TOOL_STICK_TO_DOCUMENTS,
  TOOL_CANVAS,
  TOOL_REGENERATE_RESPONSE,
  TOOL_ATTACHED_RESOURCES,
  TOOL_LIBRARY_RESOURCES,
  getChatTypeName,
  getChatClass
} from 'src/utils/analyticsUtils';
export const CHAT_LIST_PAGE_SIZE = 30;
export const DISCLAIMER_SHOW_TIMER = 5000; // 5 seconds

export type ChatAttachedFileProps = File & {
  id?: number;
  status_code?: string;
  rid?: string | number;
};

export const Fields = {
  Temperature: 'Temperature',
  Tokens: 'Max length (tokens)',
  Sequences: 'Stop sequences',
  Probabilities: 'Top probabilities',
  Frequency: 'Frequency penalty',
  Presence: 'Presence penalty'
};

export const InputConfig: InputBaseComponentProps = {
  inputMode: 'numeric'
  // pattern: '[0-9]*'
};

export const SliderConfig: SliderProps = {
  marks: true,
  valueLabelDisplay: 'on'
};

export const isDifferentDay = (
  prevDate: Date | string,
  currDate: Date | string
) => {
  return (
    currDate.toString().split('T')[0] !== prevDate?.toString().split('T')[0]
  );
};

export const getMessageTemplate = (
  textInput: string,
  chatId: number,
  model: IAiModel,
  uploadedFiles?: ChatAttachedFileProps[]
) => [
  {
    chat_id: chatId,
    id: null,
    message: textInput,
    parent_id: null,
    ts: new Date().toISOString(),
    type: 'q',
    model_id: model?.id,
    // model_name: model?.name,
    // model_display_name: model?.displayName,
    resources: uploadedFiles
  }
];

export const getParamtersFomattedData = (
  data: any[]
): { [key: string]: string | number } => {
  let updatedData = {};
  data.forEach((item) => {
    updatedData[item.name] =
      item.data_type === 'text' ? item.value : parseFloat(item.value);
  });
  return updatedData;
};

const getFormattedPatchParameters = (
  data: { [z: string]: number | string }[],
  updated: { [key: string]: number | string }
): string => {
  let updatedData: { id: number | string; v: string }[] = data.map((item) => {
    return { id: item.id, v: `${updated[item.name]}` };
  });
  return JSON.stringify(updatedData);
};

export const chatListApiParams = (
  pageNumber: number,
  filterByClass?: CHAT_CLASS
) => {
  return {
    params: {
      p: pageNumber,
      ps: CHAT_LIST_PAGE_SIZE,
      ...(filterByClass && { chat_class: filterByClass })
    }
  };
};

// No cursor = the latest page; the BE sets the page size (turns, newest first).
export const getChatMessagesApiParams = (
  chatId: number,
  cursor?: string | null
) => {
  return {
    params: { chat_id: chatId, ...(cursor && { cursor }) }
  };
};

export const getUserProfileParams = (
  user: ILoggedInUser,
  isCustomInstructionsEnabled: boolean,
  responseCustomization: string,
  userPersonalization: string,
  lookups: { [lookup_type: string]: any[] }
) => {
  const {
    accountType,
    username,
    email,
    id,
    name,
    country,
    department,
    title,
    companyName
  } = user;

  const accountTypeLabel = lookups[LOOKUP.ACCOUNT_TYPE]?.find(
    (item) => item.code === accountType
  )?.value;
  return JSON.stringify({
    name,
    username,
    email,
    title,
    department,
    company_name: companyName,
    account_type: accountTypeLabel,
    country,
    is_custom_instructions_enabled: isCustomInstructionsEnabled,
    ...(isCustomInstructionsEnabled
      ? {
          user_personalization: userPersonalization,
          response_customization: responseCustomization
        }
      : {})
  });
};

export const getChatResourcesAPIParamsData = (
  chatFiles: ChatFile[],
  chatResourcesParams: ChatResourcesParams[]
) => {
  const chatFilesMap = new Map(chatFiles?.map((file) => [file.file_id, file]));
  const processedChatResources =
    chatResourcesParams?.map((resource) =>
      !chatFilesMap.has(resource.fileId)
        ? {
            id: resource.resourceId,
            type_id: resource.typeId,
            rid: resource.fileId,
            is_deleted: true
          }
        : {
            id: resource.resourceId,
            type_id: resource.typeId,
            rid: resource.fileId
          }
    ) || [];
  const chatResourcesParamsIds = new Set(
    chatResourcesParams?.map((resource) => resource.fileId)
  );
  const remainingChatFiles =
    chatFiles
      ?.filter((file) => !chatResourcesParamsIds.has(file.file_id))
      ?.map((file) => ({ rid: file.file_id, type_id: file.type_id })) || [];
  return [...processedChatResources, ...remainingChatFiles];
};

export const chatFileComparator = (a: ChatFile, b: ChatFile) =>
  a.id && b.id && a.id === b.id;

export const getChatParametersApiParams = (chatId: number) => {
  return {
    params: { chat_id: chatId }
  };
};

export const getUpdateParametersApiParams = (
  data,
  updatedData
): DataApiInputParams => {
  return {
    params: { params: getFormattedPatchParameters(data, updatedData) }
  };
};

export const getReactionApiParams = (
  id,
  value,
  details?: IResponseFeedback
): DataApiInputParams => {
  let rDetails: any = {};
  if (details?.feedbackId?.length) {
    rDetails.feedback_detail_ids = details.feedbackId.join(',');
  }
  if (details?.feedbackComment) {
    rDetails.feedback_comment = details.feedbackComment;
  }

  return {
    params: {
      id: id,
      feedback_id: value,
      ...rDetails
    }
  };
};

export const getHistoryStatus = (chats, chatId) => {
  if (chatId && chats.length) {
    let currentChat = chats.find((item) => item.id === chatId);
    return currentChat ? currentChat.history_context : true;
  }
  return true;
};

export const ATTACH_FILE_ICON_WIDTH = '22px';
export const ATTACH_FILE_ICON_HEIGHT = '22px';
export const UPLOADED_IMAGE_ICON_WIDTH = '266px';
export const UPLOADED_IMAGE_ICON_HEIGHT = '200px';

const preprocessBacktickExcerpts = (input: string): string => {
  return input?.replace(
    /`(\s*(?:\[(?:Excerpts?|Files?):[^\]]+\]\s*)+)`/g,
    (_, content) => content.trim()
  );
};

const preprocessFileFormat = (input: string): string => {
  return input?.replace(
    /\[Files?:\s*((?:\d+\s*(?:-\d+)?\s*,?\s*)+)\s*\]/g,
    (match, numbers) => {
      const cleanedNumbers = numbers
        ?.split(',')
        ?.map((num: string) => num.trim())
        ?.join(', ');
      return `[Excerpt: ${cleanedNumbers ?? ''}]`;
    }
  );
};

const preprocessExcerptList = (input: string): string => {
  return input?.replace(
    /\[Excerpts?:\s*((?:\d+\s*(?:-\d+)?\s*,?\s*)+)\s*\]/g,
    (match, numbers) => {
      const cleanedNumbers = numbers
        ?.split(',')
        ?.map((num: string) => num.trim())
        ?.join(', ');
      return `[Excerpt: ${cleanedNumbers ?? ''}]`;
    }
  );
};

const preprocessExcerptFormat = (input: string): string => {
  return input?.replace(
    /\[Excerpts?:\s*([0-9]+-[0-9]+)\s*,\s*Excerpts?:\s*([0-9]+-[0-9]+)\]/g,
    (match: string, range1: string, range2: string) =>
      `[Excerpt: ${range1}, ${range2}]`
  );
};

export const extractTextFromContent = (input: unknown): string => {
  if (typeof input === 'string') return input;
  if (Array.isArray(input)) {
    return input
      .map((block) => {
        if (typeof block === 'string') return block;
        if (
          block &&
          typeof block === 'object' &&
          (block as any).type === 'text' &&
          typeof (block as any).text === 'string'
        ) {
          return (block as any).text;
        }
        return '';
      })
      .join('');
  }
  return '';
};

export const addButtonsAfterDocument = (text: string | unknown): string => {
  let transformedInput = preprocessBacktickExcerpts(
    extractTextFromContent(text)
  );
  transformedInput = preprocessFileFormat(transformedInput);
  transformedInput = preprocessExcerptList(transformedInput);
  transformedInput = preprocessExcerptFormat(transformedInput);
  transformedInput = transformedInput?.replace(/, Excerpt: /g, ', ');

  const excerptPattern =
    /\[Excerpts?:\s*([0-9]+(?:-[0-9]+)?(?:,\s*[0-9]+(?:-[0-9]+)?)*)\s*\]/g;

  return transformedInput?.replace(
    excerptPattern,
    (match: string, excerpt: string) => {
      const transformedExcerpt = excerpt
        ?.split(',')
        ?.map((item: string) => {
          item = item.trim();
          if (item.includes('-')) {
            const transformedRange = item
              ?.split('-')
              ?.map((num: string, index: number) =>
                index === 0
                  ? `[Document: ${num}](#button)`
                  : `-[Document: ${num}](#button)`
              )
              .join('');
            return transformedRange;
          } else {
            return `[Document: ${item}](#button)`;
          }
        })
        ?.join(', ');

      return transformedExcerpt ?? '';
    }
  );
};

export const sortFiles = (formattedMessage) => {
  const pattern = /\[(Document|Excerpts?|Files?):\s*(.*?)\]/gi;
  // Strip backtick wrappers so backtick-wrapped excerpts are still counted for ordering
  const response = preprocessBacktickExcerpts(
    extractTextFromContent(formattedMessage?.response) || ''
  );
  const matches = response.match(pattern);

  if (!matches) return formattedMessage?.files;

  const orderMap = new Map();
  let order = 0;
  for (const match of matches) {
    const documentIds = match?.match(/\d+/g)?.map(Number);
    if (!documentIds) continue;
    for (const documentId of documentIds) {
      if (!orderMap.has(documentId)) {
        orderMap.set(documentId, order++);
      }
    }
  }
  return formattedMessage?.files?.sort((a, b) => {
    const orderA = orderMap.get(Number(a.document_id)) ?? Infinity;
    const orderB = orderMap.get(Number(b.document_id)) ?? Infinity;
    return orderA - orderB;
  });
};

export const removeAIMessage = (text: string | unknown) => {
  const input = extractTextFromContent(text);
  const aiRegex = /\[AI.+?:\s*\d+.*?\]/gi;
  const taskRegex =
    /\[Task:\s*((Task\s*\d+|\d+)(\s*,\s*(Task\s*\d+|\d+))*)\]/gi;
  let output = input?.replace(/\[Task:\s*\d+,\s*/g, '['); // For patterns like: [Task: 4, ...
  output = output?.replace(taskRegex, '');
  return output?.replace(aiRegex, '');
};

export const removeHumanMessage = (text: string | unknown) => {
  const input = extractTextFromContent(text);
  const regex = /\[HumanMessage:\s*\d+\]/gi;
  return input?.replace(regex, '');
};

export const getShareChatApiParams = (chatId: number, resourceType: string) => {
  return {
    params: { r_id: chatId, r_type: resourceType }
  };
};

export const getShareAssistantApiParams = (
  rid: number,
  role: string,
  resourceType: string
) => {
  return {
    params: { r_id: rid, role: role, r_type: resourceType }
  };
};

export const getPublicResourceApiParams = (id: string) => {
  return {
    params: { share_id: id }
  };
};

export const getSendMsgAPIParams = (
  queryText: string,
  resourceId: number,
  chat_history: ChatHistoryItem[],
  newChatResourcesData: any,
  regenerateMsgObj: RegenerateMsgObj,
  user: ILoggedInUser,
  isCustomInstructionsEnabled: boolean,
  responseCustomization: string,
  userPersonalization: string,
  lookups: { [lookup_type: string]: any[] },
  starterQuestion: string,
  chatId: number,
  chatContexts: any,
  hasAssistant: boolean,
  assistantContext: AssistantContextType,
  isDataAnalyst: boolean,
  isEmpowerGPTChat: boolean,
  chatTransactionId: string,
  currentFilesUploadingCount: number,
  uploadedFiles: ChatAttachedFileProps[],
  connectionId: string,
  assistantObj: AssistantType,
  stickToDocuments: boolean,
  openedDocuments: number[],
  isHistoryLinked: boolean,
  isWebSearch: boolean,
  isProSearch: boolean,
  isImageGeneration: boolean,
  imageGenerationTool: string,
  tagsList: any[],
  canvasMsgObj: CanvasMsgObj,
  knowledge_type?: string
) => {
  const isProjectsChat =
    chatContexts[chatId]?.documentContext?.type === RESOURCE_TYPE.PROJECT;
  const resolvedChatClass = isProjectsChat
    ? chatContexts?.[chatId]?.chat_class
    : undefined;

  const apiParams: DataApiInputParams = {
    params: {
      knowledge_type,
      user_profile: getUserProfileParams(
        user,
        isCustomInstructionsEnabled,
        responseCustomization,
        userPersonalization,
        lookups
      ),
      msg: starterQuestion || queryText,
      chat_id: chatId || '',
      ...((chatContexts[chatId]?.documentContext?.id || hasAssistant) &&
        !assistantContext?.isPreview && {
          rid: resourceId
        }),
      ...(!chatId &&
        chatTransactionId && {
          chat_folder_name: chatTransactionId
        }),
      ...(!!currentFilesUploadingCount && {
        new_files: JSON.stringify(
          sanitizedFiles(uploadedFiles, currentFilesUploadingCount)
        )
      }),
      wps_conn_id: connectionId,
      chat_type: hasAssistant
        ? assistantObj?.isWebSearch || assistantObj?.worldKnowledge
          ? CHAT_SOURCE_TYPES.ChatGPT
          : CHAT_SOURCE_TYPES.EmpowerGPT
        : chatContexts[chatId].chat_type,
      ...(stickToDocuments && {
        document_context: openedDocuments.toString()
      }),
      ...(!isHistoryLinked &&
        !(assistantObj?.isWebSearch || assistantObj?.worldKnowledge) && {
          disable_history: !isHistoryLinked
        }),
      model_id: hasAssistant
        ? assistantObj?.aiModel?.id
        : chatContexts[chatId].chatModel?.id,
      web_search_enabled: isWebSearch,
      ...(!isEmpowerGPTChat && { pro_search_enabled: isProSearch }),
      image_generation_enabled: isImageGeneration,
      ...(isImageGeneration && { image_generation_tool: imageGenerationTool }),
      chat_class:
        resolvedChatClass ??
        (isDataAnalyst
          ? CHAT_CLASS.DATA_ANALYST
          : hasAssistant
          ? CHAT_CLASS.ASSISTANT
          : CHAT_CLASS.NORMAL),
      ...(hasAssistant &&
        assistantContext?.isPreview && {
          preview: JSON.stringify({
            ...assistantObj,
            model_id: assistantObj?.aiModel?.id,
            rid_list: assistantObj?.fileList,
            question: starterQuestion || queryText,
            chat_history: chat_history
          })
        }),
      chat_resources: JSON.stringify(
        isEmpowerGPTChat ? newChatResourcesData : []
      ),
      ...(regenerateMsgObj &&
        regenerateMsgObj?.currentMessage?.id && {
          resume: JSON.stringify(regenerateMsgObj?.resume || {}),
          msg_id: regenerateMsgObj.currentQuery.id,
          regenerate_response_id: regenerateMsgObj.currentMessage.id,
          ...(!regenerateMsgObj?.updatedQueryInput &&
            !!regenerateMsgObj.updatedModel && {
              regenerate_model_id: regenerateMsgObj.updatedModel?.id
            })
        }),
      ...(!hasAssistant &&
        typeof canvasMsgObj?.canvasId === 'number' && {
          canvas: JSON.stringify({
            canvas_id: canvasMsgObj?.canvasId,
            canvas_content: canvasMsgObj?.canvasContent
          })
        })
    }
  };

  if (tagsList.length) {
    apiParams.params.tag_id = tagsList
      .map((tag) => (typeof tag === 'string' ? tag : tag?.id))
      .toString();
  }
  return apiParams;
};

export const splitLastWordFromString = (
  str: string
): { remainingString: string; lastWord: string } => {
  let i;
  for (i = str.length - 1; i >= 0; i--) {
    if ([' ', '\n'].includes(str[i])) {
      break;
    }
  }

  return {
    remainingString: str.substring(0, i + 1),
    lastWord: str.substring(i + 1)
  };
};

export enum KEYBOARD_EVENT {
  DOWN = 'ArrowDown',
  UP = 'ArrowUp',
  ENTER = 'Enter'
}

export const chatErrors = [
  'CONTENT_FILTER',
  'GENERAL_ERROR',
  'UNSUPPORTED_LANGUAGE',
  'NO_AUTHORISED_FILES',
  '429',
  'CONTEXT_LENGTH_EXCEEDED'
];
export const groupChatsByDate = (chats: ChatData[]) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  const groupedChats = groupBy(chats, (chat) => {
    const chatDate = new Date(chat.ts);
    chatDate.setHours(0, 0, 0, 0);

    if (chatDate.getTime() === today.getTime()) {
      return 'today';
    } else if (chatDate >= startOfWeek) {
      return 'thisWeek';
    } else {
      return 'thisMonthAndOlder';
    }
  });

  return {
    today: groupedChats.today || [],
    thisWeek: groupedChats.thisWeek || [],
    thisMonthAndOlder: groupedChats.thisMonthAndOlder || []
  };
};

export const preprocessMathExpressions = (content: string | unknown) => {
  const input = extractTextFromContent(content);
  return input

    ?.replace(/(?<!\\)\$(?=\s?\d)/g, () => '\\$')
    ?.replace(/\\\[(\s*[\s\S]*?)\\\]/g, '$$$1$$')
    ?.replace(/\\\((.*?)\\\)/g, '$$$1$$');
};

export const getChatTypeText = (
  chatClass: string,
  chatType: string,
  currentAssistantDetails: StringKeys,
  t: TFunction<'translation', undefined>,
  isAssistantDeleted?: boolean
) => {
  const isAssistantChat = chatClass === CHAT_CLASS.ASSISTANT;

  if (isAssistantChat) {
    return isAssistantDeleted
      ? t(T.assistantDeleted)
      : currentAssistantDetails?.name;
  }

  if (chatType === CHAT_SOURCE_TYPES.ChatGPT) {
    return t(T.worldKnowledge);
  }

  return t(T.tenantKnowledgeBase, {
    tenant: TENANT_NAME || t(T.enterprise)
  });
};

export const ASSISTANT_ID = 'assistantId';

export const languageMap = ['en-US', 'de-DE', 'tr-TR'];

export type OnSendChatProps = (
  input: string,
  setInput: (t: string) => void,
  regenerateMsgObj?: RegenerateMsgObj,
  canvasMsgObj?: CanvasMsgObj
) => void;

/**
 * Checks if there is live image generation data available for a specific message
 * @param messageRid - The RID of the message to check
 * @param imageGenerationDataMap - The map of image generation data from context
 * @returns boolean indicating if live image data exists for this message
 */
export const hasLiveImageGenerationData = (
  messageRid: number | undefined,
  imageGenerationDataMap: Record<number, any[]> | undefined
): boolean => {
  if (!messageRid || !imageGenerationDataMap) {
    return false;
  }

  const liveImageData = imageGenerationDataMap[messageRid];
  return (
    liveImageData && Array.isArray(liveImageData) && liveImageData.length > 0
  );
};
